<template>
    <div class="gp-ba">
        <div class="gp-ba__container">
            <h4
                class="gp-ba__title"
                v-text="$ml.get('personalDataSubtitle')"
                tabindex="0"
            />

            <div class="gp-ba__wrapper--identity">
                <CieloSelectV2
                    @change="selectDocumentType"
                    :title="$ml.get('documentType')"
                    :label="$ml.get('selectDocumentType')"
                    :option-items="documentTypes"
                    :selected-item="selectedDocumentType"
                    other-class="cielo-select__container--documentType"
                    :can-translate="true"
                />
                <CieloInputV2
                    style="width: 100%; margin-bottom: 0px"
                    :autofocus="false"
                    :borderless="false"
                    name="Identity"
                    :label="$ml.get('identity')"
                    v-model="Identity"
                    v-validate="identityValidationRule"
                    :type="identityInputType"
                    :mask="identityMask"
                    :data-vv-as="$ml.get('identity')"
                    :error="errors.first('Identity')"
                />
            </div>

            <CieloInputV2
                :autofocus="false"
                :borderless="false"
                name="Phone"
                :label="$ml.get('cellPhone')"
                v-model="Phone"
                v-validate="phoneValidationRule"
                :data-vv-as="$ml.get('cellPhone')"
                type="tel"
                :error="errors.first('Phone')"
                :mask="phoneMask"
                v-show="!hasPhoneNumber"
            />

            <h4
                class="gp-ba__title gp-ba__title--billing-address"
                v-text="$ml.get('billingAddress')"
                tabindex="0"
            />

            <SameShippingAddress
                v-if="productHasShipping"
            />

            <BillingAddress :address-errors="addressErrors" />
        </div>

        <div class="gp-ba__cta-container">
            <CieloCheckBoxV2
                class="justify-content-start"
                v-model="agreement"
                :clickable-slot="true"
                :text-aria="$ml.get('termsPreffix') + $ml.get('termsSuffix')"
            >
                <span v-text="$ml.get('termsPreffix')" />&nbsp;
                <a target="_blank" :href="showTerms" v-text="$ml.get('termsSuffix')" tabindex="0" />
            </CieloCheckBoxV2>
            <ButtonsFinishPaymentV2
                :disable-button-next="disableButtonNext"
                :loading="loading"
                @click-next="finishPayment"
                @click-previous="goToPreviousStep"
            />
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';

    import ProductHelper from '@/helpers/productHelper';
    import PaymentProcessor from '@/services/payment/PaymentProcessor';
    import vars from '@/config/vars';
    import CpfCnpjValidation from '@/helpers/validation-rules/CpfCnpjValidation';

    const CieloInputV2 = () =>
        import(
            /* webpackChunkName: "cielo-input" */ '@/components/ClickToPay/template/CieloInputV2'
        );
    import ButtonsFinishPaymentV2 from '@/components/template/ButtonsFinishPaymentV2';
    import CieloCheckBoxV2 from '@/components/ClickToPay/template/CieloCheckBoxV2';
    import CieloSelectV2 from '@/components/ClickToPay/template/CieloSelectV2/CieloSelectV2';
    import SameShippingAddress from '@/components/Billing/SameShippingAddress';
    import BillingAddress from '@/components/Billing/BillingAddress';


    export default {

      components: {
          ButtonsFinishPaymentV2,
          CieloCheckBoxV2,
          CieloSelectV2,
          CieloInputV2,
          SameShippingAddress,
          BillingAddress,
      },

      data() {
          return {
              showTerms: `${vars.CIELO_CHECKOUT_URL}/TransactionalVNext/Content/documents/Termos%20e%20condicoes%20de%20Uso_Cielo.pdf`,
              loading: false,
              agreement: false,
			  selectedDocumentType: null,
              documentTypes: [
                  { Text: 'cpf', Value: 'CPF' },
                  { Text: 'cnpj', Value: 'CNPJ' },
                  { Text: 'foreign', Value: 'FOREIGN' },
              ],
              addressErrors: {},
          };
      },

      created() {
          if(this.gpCountryCode !== 'BR') {
              this.$store.commit('billingAddress/setType', 'foreign');
          }
          this.$store.dispatch('googlePayPayment/setBillingAddress');
          this.$validator.extend('cpf-cnpj', CpfCnpjValidation);
          this.selectedDocumentType = this.IdentityType;
      },

      computed: {
		...mapGetters({
			mainState: 'googlePayPayment/getState',
			isNational: 'billingAddress/getIsNational',
			gpCountryCode: 'googlePayPayment/getBillingAddressCountryCode',
			sameAsShippingAddress: 'billingAddress/getSameAsShippingAddress',
			hasPhoneNumber: 'googlePayPayment/getHasPhoneNumber',
			billingAddress: 'billingAddress/getBillingAddress',
		}),

		Identity: {
			get: (self) => self.$store.state.customer.Identity,
			set(value) {
				this.$store.commit('customer/setIdentity', value);
			},
		},
		IdentityType: {
			get: (self) => self.$store.state.customer.IdentityType,
			set(value) {
				this.$store.commit('customer/setIdentityType', value);
			},
		},
		Phone: {
			get: (self) => self.$store.state.googlePayPayment.consumer.phone,
			set(value) {
				this.$store.commit('googlePayPayment/setPhone', value);
			},
		},

		productHasShipping: () => ProductHelper.productHasShipping(),

		phoneValidationRule() {
			return ['CPF', 'CNPJ'].includes(this.IdentityType) ? 'required|numeric|min:11' : 'required|numeric|max:16';
		},
		phoneMask() {
			return ['CPF', 'CNPJ'].includes(this.IdentityType) ? '(##) #####-####' : null;
		},

		identityValidationRule() {
			switch (this.IdentityType) {
				case 'CPF':
					return 'required|numeric|min:11|max:11|cpf-cnpj';
				case 'CNPJ':
					return 'required|numeric|min:14|max:14|cpf-cnpj';
				default:
					return 'required|max:32';
			}
		},
		identityMask() {
			switch (this.IdentityType) {
				case 'CPF':
					return ['###.###.###-##'];
				case 'CNPJ':
					return ['##.###.###/####-##'];
				default:
					return null;
			}
		},
		identityInputType() {
			return ['CPF', 'CNPJ'].includes(this.IdentityType) ? 'tel' : 'text';
		},

		isBillingFormValid() {

			if(this.sameAsShippingAddress) return false;

			const address = this.billingAddress;
			
			if (this.isNational) {
				return (
					!address.Street ||
					!address.State ||
					!address.City ||
					!address.Number ||
					!address.District ||
					!address.ZipCode
				);
			} else {
				return (
					!address.Street ||
					!address.State ||
					!address.City ||
					!address.ZipCode
				);
			}
		},

		disableButtonNext() {
			const missingCommonFields = !this.agreement || !this.Identity || !this.Phone;
			const missingBillingAddressFields = this.isBillingFormValid;
			const hasErrors = this.errors.items.length > 0;

			return (
				missingCommonFields ||
				missingBillingAddressFields ||
				hasErrors
			);
		},
      },

      methods: {

		selectDocumentType(value) {
			this.IdentityType = value;
		},

		goToPreviousStep() {
			this.$store.dispatch('pipelineV2/pushTo', 'GooglePayCardInstallments');
		},

		async finishPayment() {
			if(!this.loading) {
				this.loading = true;
				await PaymentProcessor.Process();
				this.loading = false;
			}
		},
      },

    };
</script>
<style lang='scss' scoped>
    .gp-ba {

        @media screen and (max-width: 1024px) {
            display: flex;
            flex-direction: column;
            flex: 1 0 auto;
        }

        &__container {

            @media screen and (max-width: 1024px) {
                flex: 1;
            }
        }

        &__wrapper--identity {
            display: flex;
            flex-direction: column;
            gap: 20px;
            width: 100%;
            margin-bottom: 20px;

            @media screen and (min-width: 1025px) {
                flex-direction: row;
            }
        }

        &__wrapper--billing-address {}

        &__cta-container {}

        &__title {
            font-size: 16px;
            font-weight: 700;
            line-height: 16px;
            color: #2F363E;
            margin-bottom: 24px
        }

        &__title--billing-address {
            margin-top: 32px;
        }

        &__select-title {
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: 11px;
            color: #2F363E;
            margin-bottom: 20px;
        }

        .cielo-checkbox {
            margin: 24px 0px;
        }
    }
</style>
